import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "../../../../ui/badge";
import { Button } from "../../../../ui/button";
import { Card, CardContent } from "../../../../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../ui/table";
import { Loader2 } from "lucide-react";

import { ScenarioHeader } from "../../../../components/portfolio-risk-concentration/ScenarioHeader";
import { ScenarioSpinner } from "../../../../components/portfolio-risk-concentration/ScenarioSpinner";
import { ScenarioStepDialog } from "../../../../components/portfolio-risk-concentration/ScenarioStepDialog";
import { usd } from "../../../../components/portfolio-risk-concentration/format";
import {
  conditionSummary,
  useResetScenarioAnswers,
  useRunScenarioWaterfall,
  useScenarioInvestorCodes,
  type ScenarioCondition,
  type ScenarioContract,
  codesLabel,
} from "../../../../utils/portfolio-risk-concentration/api/scenario";
import type { ScenarioDraft } from "../ScenarioSimulatorPage";

function destinationLabel(condition: ScenarioCondition): string {
  if (!condition.destination) return "Exclude";
  return `Code ${condition.destination.investorCode} · ${condition.destination.facilityCode}`;
}

const CONTRACTS_PAGE_SIZE = 10;

/** The "View contracts" expansion: an inline, paginated table of exactly the contracts one
 *  condition matched. Client-side pagination: draft.contracts is already fully loaded, and a
 *  condition can match thousands of rows, so rendering the whole matched set at once would be
 *  unusable. Each card mounts its own instance, so page state never leaks between cards. */
function MatchedContractsTable({
  contracts,
  matchedKeys,
}: {
  contracts: ScenarioContract[];
  matchedKeys: string[];
}) {
  const [offset, setOffset] = useState(0);

  const matched = useMemo(() => {
    const keySet = new Set(matchedKeys);
    return contracts.filter((c) => keySet.has(c.contractScheduleKey));
  }, [contracts, matchedKeys]);

  const total = matched.length;
  const page = matched.slice(offset, offset + CONTRACTS_PAGE_SIZE);

  return (
    <div className="mt-3 overflow-hidden rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Contract #</TableHead>
            <TableHead>Schedule #</TableHead>
            <TableHead className="text-right">Financed Amount</TableHead>
            <TableHead className="text-right">Net Investment</TableHead>
            <TableHead>Booked Date</TableHead>
            <TableHead>Asset Industry</TableHead>
            <TableHead>Borrower Industry</TableHead>
            <TableHead>Rating</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {page.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                Nothing matched.
              </TableCell>
            </TableRow>
          )}
          {page.map((c) => (
            <TableRow key={c.contractScheduleKey}>
              <TableCell className="whitespace-nowrap text-xs">
                {c.contractNumber ?? "—"}
              </TableCell>
              <TableCell className="text-xs">{c.scheduleNumber ?? "—"}</TableCell>
              <TableCell className="text-right">{usd(c.financedAmount)}</TableCell>
              <TableCell className="text-right">{usd(c.netInvestmentDollars)}</TableCell>
              <TableCell className="whitespace-nowrap text-xs">{c.dateBooked ?? "—"}</TableCell>
              <TableCell className="text-xs">{c.assetIndustry ?? "—"}</TableCell>
              <TableCell className="text-xs">{c.sicCode ?? "—"}</TableCell>
              <TableCell className="text-xs">{c.riskRating ?? "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
        <span>
          {total === 0 ? "0 of 0" : `${offset + 1}–${Math.min(offset + CONTRACTS_PAGE_SIZE, total)} of ${total}`}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - CONTRACTS_PAGE_SIZE))}
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={offset + CONTRACTS_PAGE_SIZE >= total}
            onClick={() => setOffset((o) => o + CONTRACTS_PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

/** 2 · Scenario: the waterfall. Each condition consumes only what earlier conditions in
 *  the list didn't already claim; see the backend's own module docstring for why. Every
 *  condition is added or edited through ScenarioStepDialog, no inline field editing here,
 *  and no free-text destination entry; Destination is always a real (code, facility) pair or
 *  Exclude, picked in that dialog. */
export default function ScenarioApplyStep({
  draft,
  patch,
  base,
}: {
  draft: ScenarioDraft;
  patch: (p: Partial<ScenarioDraft>) => void;
  base: string;
}) {
  const navigate = useNavigate();
  const waterfall = useRunScenarioWaterfall();
  const resetAnswers = useResetScenarioAnswers();
  const codesQ = useScenarioInvestorCodes();
  // Memoized so its reference only changes when codesQ.data actually changes -- used as a
  // useEffect dependency below, and a fresh array every render would re-fire that effect on
  // every unrelated re-render rather than only once, when the codes genuinely arrive.
  const destinationCodes = useMemo(
    () => (codesQ.data ?? []).filter((c) => c.code !== draft.sourceInvestorCode),
    [codesQ.data, draft.sourceInvestorCode],
  );

  // A NEW scenario starts with no filters at all: one condition with no clauses, and the viewer adds whichever filters they want.
  // The draft already carries conditions when an approved scenario was opened from Scenario Portfolio (its own conditions and
  // destinations), and coming back to this step keeps the ones already built.
  const [rows, setRows] = useState<ScenarioCondition[]>(() =>
    draft.conditions.length > 0 ? draft.conditions : [{ clauses: [], destination: null }],
  );
  const startedWithConditions = useRef(draft.conditions.length > 0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (draft.contracts.length === 0) navigate(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function recompute(next: ScenarioCondition[]) {
    setRows(next);
    const result = await waterfall.mutateAsync({ contracts: draft.contracts, conditions: next });
    // The Monitor and Snapshot answers are computed FROM this waterfall's assignments and kept per filter combination (see
    // useScenarioMonitorAnswer), so a condition added, edited or given another destination drops them all here, at the source: the next
    // visit to either step asks again, instead of showing the pre-edit numbers.
    resetAnswers();
    patch({ conditions: next, waterfall: result });
  }

  function openAdd() {
    setEditIndex(null);
    setDialogOpen(true);
  }

  function openEdit(i: number) {
    setEditIndex(i);
    setDialogOpen(true);
  }

  function handleDialogSubmit(condition: ScenarioCondition) {
    const next =
      editIndex === null
        ? [...rows, condition]
        : rows.map((r, idx) => (idx === editIndex ? condition : r));
    void recompute(next);
  }

  // Run once on mount so the very first condition's numbers show without an extra click. This
  // fires immediately (destination codes may not have loaded yet), so the auto-created first
  // condition starts out with the placeholder's destination: null -- the effect below upgrades
  // it to a real destination the moment codes are available, rather than delaying this one and
  // risking it never firing if the codes query is slow or fails.
  useEffect(() => {
    if (draft.contracts.length > 0 && !draft.waterfall) void recompute(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.contracts.length]);

  // Upgrades the auto-created first condition's destination from the placeholder's null
  // (Exclude) to a real one — first destination code, CAP1 — the same "not excluded by
  // default" default the Add/Edit dialog itself seeds, once destination codes load. Keyed on
  // the destination alone, not on whether clauses are still empty: this condition's clauses
  // start with no clauses, so checking for empty clauses here would not tell a placeholder from a
  // configured condition. Spreads the
  // existing row rather than rebuilding it, so its clauses (defaults, or whatever the viewer
  // has already changed them to) are carried through untouched — only destination moves.
  // Still only touches a row that has never had a destination explicitly set; one the viewer
  // has already configured or saved (via the dialog, or a prior run of this effect) is left
  // alone.
  useEffect(() => {
    if (destinationCodes.length === 0) return;
    // conditions that arrived with the draft already have the destinations they were approved with
    if (startedWithConditions.current) return;
    if (rows.length !== 1 || rows[0].destination) return;
    // the first code that belongs to a facility, with that facility; with none mapped yet there is nothing to default to and the viewer picks
    const first = destinationCodes.find((c) => c.facilities.length > 0);
    if (!first) return;
    void recompute([{ ...rows[0], destination: { investorCode: first.code, facilityCode: first.facilities[0] } }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationCodes]);

  const remainder = draft.waterfall?.remainder;
  const assignedTotal = (draft.waterfall?.conditions ?? [])
    .filter((c) => c.destination)
    .reduce((sum, c) => sum + c.dollarTotal, 0);
  // The scenario total IS the existing portfolio: the source pool is made of contracts already in it (same Owned + considered population),
  // and assigning them to a facility moves them, it adds nothing. Adding the assigned dollars on top counted them twice. Null while loading.
  const scenarioTotal = draft.existingPortfolioDollars;

  return (
    <div>
      <ScenarioHeader
        title="Scenario"
        subtitle={`${draft.approvedScenarioLabel ? `Approved scenario ${draft.approvedScenarioLabel} · ` : ""}${draft.sourceKeys !== null ? `Uploaded contracts, effective ${draft.poolFileDate ?? draft.asOfDate}` : `${draft.dateFrom} – ${draft.dateTo}`} · ${usd(draft.contracts.reduce((s, c) => s + c.netInvestmentDollars, 0))} in Investor ${codesLabel(draft.sourceInvestorCode)}`}
      />
      <p className="pt-2 max-w-2xl text-sm text-muted-foreground">
        Each step acts on whatever the step above it didn't take. Click a step to see the
        contracts in it.
      </p>

      {waterfall.isPending && !draft.waterfall ? (
        <ScenarioSpinner label="Running scenario…" />
      ) : (
        <>
          {/* A later recompute (adding/editing a condition) keeps the existing, now-stale
              cards on screen rather than replacing them with a spinner — losing the whole
              list on every edit would be more disruptive than a moment of stale numbers. */}
          {waterfall.isPending && (
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Recalculating…
            </div>
          )}

          <div className="mt-4 space-y-3">
            {rows.map((row, i) => {
              const result = draft.waterfall?.conditions[i];
          const isExpanded = !!expanded[i];
          return (
            <Card key={i}>
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-start gap-4">
                    <span className="mt-1 flex h-6 w-6 items-center justify-center rounded-full border border-border text-[11px] font-semibold text-muted-foreground">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground">Condition</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {conditionSummary(row, result?.contractCount).map((s, idx) => (
                          <Badge key={idx} variant="outline" className="text-[10px]">
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground">Destination</p>
                      <Badge
                        variant="outline"
                        className={
                          "mt-1 text-[10px] " +
                          (row.destination
                            ? "border-amber/50 text-amber"
                            : "border-destructive/50 text-destructive")
                        }
                      >
                        {destinationLabel(row)}
                      </Badge>
                    </div>

                    <div className="text-right">
                      <p className="text-sm font-semibold text-amber">
                        {result ? usd(result.dollarTotal) : "—"}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {result ? `${result.contractCount} contracts` : ""}
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[11px]"
                        onClick={() => openEdit(i)}
                      >
                        Adjust
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[11px] text-primary"
                        onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))}
                      >
                        {isExpanded ? "Hide contracts ▴" : "View contracts ▾"}
                      </Button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <MatchedContractsTable contracts={draft.contracts} matchedKeys={result?.matchedKeys ?? []} />
                )}
              </CardContent>
            </Card>
          );
            })}
          </div>

          <Button variant="ghost" size="sm" className="mt-3" onClick={openAdd}>
            + Add scenario
          </Button>
        </>
      )}

      <ScenarioStepDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editCondition={editIndex === null ? null : rows[editIndex]}
        sourceInvestorCode={draft.sourceInvestorCode}
        onSubmit={handleDialogSubmit}
      />

      <Card className="mt-4">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
          <div className="flex gap-8 text-xs">
            <div>
              <p className="text-muted-foreground">Remaining in {codesLabel(draft.sourceInvestorCode)}</p>
              <p className="font-semibold">{remainder ? usd(remainder.dollarTotal) : "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Assigned this session</p>
              <p className="font-semibold">{usd(assignedTotal)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Scenario total</p>
              <p className="font-semibold">{scenarioTotal === null ? "—" : usd(scenarioTotal)}</p>
            </div>
          </div>
          <Button disabled={!draft.waterfall} onClick={() => navigate(`${base}/monitor`)}>
            View Scenario Monitor →
          </Button>
        </CardContent>
      </Card>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Every amount on this page is book value (net investment). Scenario Monitor starts on Present Value (DCB), so its code cards read
        lower for the same contracts; set Measured in to Book Value there to see the same figures as here.
      </p>
    </div>
  );
}
